import {
  addToast,
  Button,
  Modal,
  Select,
  SelectOptionType,
  TextInput,
} from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { IdentificationSearchFilterValue } from '../../../../components/IdentificationSearchFilter/types';
import { useActionKitImp } from '../../../../components/ozmoit/common';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { registerPaymentAction } from '../actions/actions';

export type RegisterPaymentModalMethods = {
  open: (
    debtIds: unknown[],
    query: Partial<IdentificationSearchFilterValue>,
  ) => void;
  close: () => void;
};

type RegisterPaymentModalProps = {
  onSave?: () => void;
};

type FormFields = {
  paymentMethod: SelectOptionType | null;
  last4CardDigits?: string | null;
  authorizationCode: string;
  documentType: SelectOptionType | null;
  email: string;
};

const DEFAULT_VALUES: FormFields = {
  paymentMethod: null,
  last4CardDigits: null,
  authorizationCode: '',
  documentType: null,
  email: '',
};

const RegisterPaymentModal = (
  { onSave }: RegisterPaymentModalProps,
  ref: Ref<RegisterPaymentModalMethods>,
) => {
  const { msgValidations, validateEmail } = useValidations();
  const { t } = useTranslation(TranslationsKeys.PAYMENT_HUB_DEBTS);
  const [debtIds, setDebtIds] = useState<unknown[]>([]);
  const [query, setQuery] = useState<Partial<IdentificationSearchFilterValue>>(
    {},
  );

  const { control, handleSubmit, reset, watch } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const { action: registerPayment, loading } = useActionKitImp(
    registerPaymentAction,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t(`registerPaymentModal.success`),
        });
        !!onSave && onSave();
        setDebtIds([]);
        setQuery({});
      },
      onError: {
        unknown: (error) => {
          if (error?.data instanceof Error) {
            addToast({
              color: 'danger',
              text: error.data.message,
            });
          } else {
            addToast({
              color: 'danger',
              text: t(`registerPaymentModal.failed`),
            });
          }
        },
      },
    },
  );

  const paymentMethod = watch('paymentMethod');

  const requiresLast4CardDigits = useMemo(() => {
    return paymentMethod?.value === 'TC' || paymentMethod?.value === 'TD';
  }, [paymentMethod]);

  const paymentMethodOptions: SelectOptionType[] = useMemo(() => {
    return [
      { label: t('paymentMethods.EF'), value: 'EF' },
      {
        label: t('paymentMethods.TC'),
        value: 'TC',
      },
      {
        label: t('paymentMethods.TD'),
        value: 'TD',
      },
    ];
  }, [t]);

  const documentTypeOptions: SelectOptionType[] = useMemo(() => {
    return [
      { label: t('documentTypes.boleta'), value: 'BO' },
      {
        label: t('documentTypes.factura'),
        value: 'FA',
      },
    ];
  }, [t]);

  const handleOpen = useCallback(
    (ids: unknown[], q: Partial<IdentificationSearchFilterValue>) => {
      reset(DEFAULT_VALUES);
      setDebtIds(ids);
      setQuery(q);
    },
    [reset],
  );

  const handleClose = useCallback(() => {
    setDebtIds([]);
    setQuery({});
  }, []);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSave = useCallback(
    async (data: FormFields) => {
      if (loading) {
        return;
      }

      await registerPayment({
        ...query,
        debtIds,
        paymentMethod: data.paymentMethod?.value as string,
        documentType: data.documentType?.value as string,
        authorizationCode: data.authorizationCode,
        last4CardDigits: data.last4CardDigits,
        email: data.email,
      });
    },
    [loading, registerPayment, debtIds, query],
  );

  return (
    <Modal isOpen={!!debtIds.length} size="md" toggle={handleClose}>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h2 className="text-dark fs-22 mb-0 w-100 text-center">
              {t(`registerPaymentModal.title`)}
            </h2>
            <p className="fs-16 w-100 text-center">
              {t(`registerPaymentModal.description`)}
            </p>
          </Col>
        </Row>
        <Row className="my-5">
          <Col xs={12}>
            <Select
              control={control}
              name="paymentMethod"
              options={paymentMethodOptions}
              label={t(`registerPaymentModal.inputs.paymentMethod.label`)}
              placeholder={t(
                `registerPaymentModal.inputs.paymentMethod.placeholder`,
              )}
              isClearable={false}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          {!!requiresLast4CardDigits && (
            <Col xs={12}>
              <TextInput
                control={control}
                name="last4CardDigits"
                label={t(`registerPaymentModal.inputs.last4CardDigits.label`)}
                placeholder={t(
                  `registerPaymentModal.inputs.last4CardDigits.placeholder`,
                )}
                rules={{ required: msgValidations.required }}
                formatter={(v) => v.replace(/[^\d]/g, '').slice(0, 4)}
                disabled={loading}
                maxLength={4}
              />
            </Col>
          )}
          <Col xs={12}>
            <TextInput
              control={control}
              name="authorizationCode"
              label={t(`registerPaymentModal.inputs.authorizationCode.label`)}
              placeholder={t(
                `registerPaymentModal.inputs.authorizationCode.placeholder`,
              )}
              rules={{ required: msgValidations.required }}
              formatter={(v) => v.replace(/[^\d]/g, '').slice(0, 6)}
              disabled={loading}
              maxLength={6}
            />
          </Col>
          <Col xs={12}>
            <Select
              control={control}
              name="documentType"
              options={documentTypeOptions}
              label={t(`registerPaymentModal.inputs.documentType.label`)}
              placeholder={t(
                `registerPaymentModal.inputs.documentType.placeholder`,
              )}
              isClearable={false}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col xs={12}>
            <TextInput
              control={control}
              name="email"
              label={t(`registerPaymentModal.inputs.email.label`)}
              placeholder={t(`registerPaymentModal.inputs.email.placeholder`)}
              rules={{
                validate: {
                  email: (e) => validateEmail(e?.toString() || undefined),
                },
              }}
              disabled={loading}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.cancel')}
              type="button"
              fullwidth
              outlined
              disabled={loading}
              onClick={handleClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              text={t('registerPaymentModal.create')}
              type="button"
              fullwidth
              onClick={handleSubmit(handleSave)}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Modal>
  );
};

export default forwardRef(RegisterPaymentModal);
